import React, { useEffect, useState } from "react";
import { Customer } from "@/api/customers";
import {
  removeApplicationCustomers,
  ApplicationCustomers,
  Application,
} from "@/api/applications";
import { Link } from "react-router-dom";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import DeleteCustomer from "@/components/customers/DeleteCustomer";
import AppCustomerUsers from "@/components/users/app-customer-users/AppCustomerUsers";
import { Configuration } from "@/config";
import AppCustomerDefaultValuesModal from "@/components/customers/AppCustomerDefaultValuesModal";


interface Props {
  Application: Application;
  CustomerList: Customer[];
  hasDefaultTypes?: boolean;
  defaultTypes?: any[];
  onRemoveCustomers?: (customerIds: number[]) => any;
  onDelete: (customer: Customer) => any;
}

enum MenuActionType {
  DELETE_APP_CUSTOMER = 1,
  DEACTIVATE_CUSTOMER = 2,
  ADD_USER = 3,
  DEFAULT_VALUES = 4,
}

const {
  config: { AUTHORIZATION_APP_ID, PCS_SOFTWARE_CUSTOMER_ID },
} = new Configuration();

const AppCustomerDetailList = (props: Props) => {
  const [selectedCustomer, setSelectedCustomer] = useState<
    Customer | undefined
  >(undefined);
  const [menuActionType, setMenuActionType] = useState<
    MenuActionType | undefined
  >(undefined);
  const countBadgeStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 32,
    height: 28,
    padding: "0 10px",
    borderRadius: 14,
    backgroundColor: "#f3f2f1",
    color: "#323130",
    fontWeight: 600,
    textDecoration: "none",
  };

  const columns: DataGridColumn[] = [
    {
      key: "id",
      name: "Id",
      fieldName: "Id",
      minWidth: 50,
      maxWidth: 60,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "name",
      name: "Name",
      fieldName: "Name",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "customerNumber",
      name: "Customer Number",
      fieldName: "CustomerNumber",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "databaseIdentifier",
      name: "Database Identifier",
      fieldName: "DatabaseIdentifier",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "environment",
      name: "Environment",
      fieldName: "Environment",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "NoUsers",
      name: "Users",
      fieldName: "UsersCount",
      minWidth: 70,
      maxWidth: 100,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: Customer) => (
        <Link
          to={`/applications/${props.Application.Id}/customers/${item.Id}/users`}
          style={countBadgeStyle}
        >
          {item.UsersCount}
        </Link>
      ),
    },
    {
      key: "Actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: Customer) => (
        <MoreActionsButton
          menu={[
            {
              key: "remove",
              text: "Remove customer from application",
              iconProps: { iconName: "Cancel" },
              disabled:
                props.Application.Id === AUTHORIZATION_APP_ID &&
                item.Id === PCS_SOFTWARE_CUSTOMER_ID,
              onClick: () => {
                removeAppCustomer(item);
              },
            },
            {
              key: "deactivate",
              text: "Deactivate customer",
              iconProps: { iconName: "Delete" },
              disabled: item.Id === PCS_SOFTWARE_CUSTOMER_ID,
              onClick: () => {
                setMenuActionType(MenuActionType.DEACTIVATE_CUSTOMER);
                setSelectedCustomer(item);
              },
            },
            {
              key: "addSection",
              text: "Add",
              iconProps: { iconName: "Add" },
              subMenuProps: {
                items: [
                  {
                    key: "addUser",
                    text: "User",
                    title: "Add application user",
                    onClick: () => {
                      setMenuActionType(MenuActionType.ADD_USER);
                      setSelectedCustomer(item);
                    },
                  },
                ],
              },
            },
            ...(props.hasDefaultTypes
              ? [
                  {
                    key: "defaultValues",
                    text: "Default Values",
                    iconProps: { iconName: "AutoFillTemplate" },
                    onClick: () => {
                      setMenuActionType(MenuActionType.DEFAULT_VALUES);
                      setSelectedCustomer(item);
                    },
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  const removeAppCustomer = (customer: Customer) => {
    removeApplicationCustomers({
      AppId: props.Application.Id,
      CustomerIds: [customer.Id],
    } as ApplicationCustomers).then(
      (_) => {
        if (props.onRemoveCustomers) props.onRemoveCustomers([customer.Id]);
      },
      (error) => {
        handleError(error);
      }
    );
  };

  const cleanCustomerAction = () => {
    setSelectedCustomer(undefined);
    setMenuActionType(undefined);
  };

  const handleError = (error: Error) => {
    // Error handling would go here in real app
  };

  const renderCustomerAction = () => {
    if (!selectedCustomer || !menuActionType) return null;

    switch (menuActionType) {
      case MenuActionType.DEACTIVATE_CUSTOMER:
        return (
          <DeleteCustomer
            customer={selectedCustomer}
            onClose={cleanCustomerAction}
            onDelete={(customer) => {
              props.onDelete(customer);
            }}
          />
        );
      case MenuActionType.ADD_USER:
        return (
          <AppCustomerUsers
            ApplicationId={props.Application.Id}
            CustomerId={selectedCustomer.Id}
            ShowOnlyNewAppCustomerUserPanel={true}
            onCloseNewAppCustomerUserPanel={cleanCustomerAction}
            onSaveAppCustomerUsers={(users) =>
              (selectedCustomer.UsersCount = users.length)
            }
          />
        );
      case MenuActionType.DEFAULT_VALUES:
        return (
          <AppCustomerDefaultValuesModal
            application={props.Application}
            customer={selectedCustomer}
            defaultTypes={props.defaultTypes || []}
            isOpen={true}
            onDismiss={cleanCustomerAction}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <DataGrid items={props.CustomerList} columns={columns} />
      {renderCustomerAction()}
    </>
  );
};

export default AppCustomerDetailList;
